// Shrink a phone photo in the browser before it is stored: a receipt photo straight from a
// phone camera is several MB; the stored copy is ~100-300 KB and a small thumbnail.
import { MAX_BYTES, MAX_EDGE, THUMB_EDGE, scaledSize } from './pure'
import type { ProofMime } from './pure'

export interface PreparedPhoto {
  blob: Blob
  thumb: Blob
  mime: ProofMime
  sizeBytes: number
  sha256: string
  width: number
  height: number
}

function toBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), mime, quality))
}

async function draw(bitmap: ImageBitmap, edge: number): Promise<{ canvas: HTMLCanvasElement; width: number; height: number }> {
  const { width, height } = scaledSize(bitmap.width, bitmap.height, edge)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot prepare photos.')
  ctx.drawImage(bitmap, 0, 0, width, height)
  return { canvas, width, height }
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Throws a message fit to show a person when the file is not a usable picture. */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (!file.type.startsWith('image/')) throw new Error('Please choose a photo (an image file).')
  let bitmap: ImageBitmap
  try {
    // imageOrientation applies the phone's rotation so receipts are not sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new Error('That file could not be read as a photo. Try taking the photo again.')
  }
  try {
    const main = await draw(bitmap, MAX_EDGE)
    // Prefer WebP (smaller); fall back to JPEG where the browser cannot encode it.
    let mime: ProofMime = 'image/webp'
    let blob = await toBlob(main.canvas, mime, 0.8)
    if (!blob || blob.type !== 'image/webp') {
      mime = 'image/jpeg'
      blob = await toBlob(main.canvas, mime, 0.8)
    }
    // Step the quality down until it fits the 2 MB limit.
    for (let q = 0.7; blob && blob.size > MAX_BYTES && q >= 0.3; q -= 0.1) blob = await toBlob(main.canvas, mime, q)
    if (!blob || blob.size > MAX_BYTES) throw new Error('That photo is too large to store. Try a smaller one.')
    const small = await draw(bitmap, THUMB_EDGE)
    let thumb = await toBlob(small.canvas, 'image/webp', 0.7)
    // A browser that cannot encode WebP hands back a PNG instead of nothing: fall back to JPEG then too.
    if (!thumb || thumb.type !== 'image/webp') thumb = await toBlob(small.canvas, 'image/jpeg', 0.7)
    if (!thumb) throw new Error('This browser cannot prepare photos.')
    return { blob, thumb, mime, sizeBytes: blob.size, sha256: await sha256Hex(blob), width: main.width, height: main.height }
  } finally {
    bitmap.close()
  }
}
